import type { MinimalUser } from '@pbk-web/types';
import type { DOMExportOutput, EditorConfig, LexicalNode, LexicalUpdateJSON, NodeKey, SerializedLexicalNode, Spread } from 'lexical';
import { $create, DecoratorNode } from 'lexical';
import type { JSX } from 'react';

import { MentionChip } from './MentionChip';

/** What a mention keeps of its user: enough to render and report it once the user has left the list. */
export type MentionUser = Pick<MinimalUser, 'id' | 'firstName' | 'lastName'>;

// `type` and `version` are contributed by SerializedLexicalNode via super.exportJSON().
export type SerializedMentionNode = Spread<{ user: MentionUser; text: string }, SerializedLexicalNode>;

const NO_USER: MentionUser = { id: '', firstName: '', lastName: '' };

/**
 * An inline, uneditable `@Firstname Lastname` tag, stored as the host's `serialise(user)` text.
 * - The user and that text are copied in when it's inserted: nodes can't read the feature config, and
 *   collaborators render it from the synced JSON alone.
 * - There's no `importDOM`: the saved span loads as text, which `registerMentionTransform` turns back into a mention.
 */
export class MentionNode extends DecoratorNode<JSX.Element> {
  __user: MentionUser;
  __text: string;

  // Defaults so the constructor takes no required arguments: Lexical synthesises
  // static clone()/importJSON() from $config() by calling `new this()`.
  constructor(user: MentionUser = NO_USER, text = '', key?: NodeKey) {
    super(key);
    this.__user = user;
    this.__text = text;
  }

  override $config() {
    return this.config('mention', {});
  }

  // Replaces the static clone() copy step: the synthesised clone default-constructs.
  override afterCloneFrom(prevNode: this): void {
    super.afterCloneFrom(prevNode);
    this.__user = prevNode.__user;
    this.__text = prevNode.__text;
  }

  getUser(): MentionUser {
    return this.getLatest().__user;
  }

  setMention(user: MentionUser, text: string): this {
    const self = this.getWritable();
    self.__user = { id: user.id, firstName: user.firstName, lastName: user.lastName };
    self.__text = text;
    return self;
  }

  override exportJSON(): SerializedMentionNode {
    return { ...super.exportJSON(), user: this.__user, text: this.__text };
  }

  override updateFromJSON(serializedNode: LexicalUpdateJSON<SerializedMentionNode>): this {
    return super.updateFromJSON(serializedNode).setMention(serializedNode.user, serializedNode.text);
  }

  override createDOM(_config: EditorConfig): HTMLElement {
    return document.createElement('span');
  }

  override updateDOM(): boolean {
    return false;
  }

  override exportDOM(): DOMExportOutput {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-mention', this.__user.id);
    element.textContent = this.__text;
    return { element };
  }

  // The plain-mode value, and what the character limit counts
  override getTextContent(): string {
    return this.__text;
  }

  override isKeyboardSelectable(): boolean {
    return true;
  }

  override isInline(): boolean {
    return true;
  }

  override isIsolated(): boolean {
    return true;
  }

  override decorate(): JSX.Element {
    return <MentionChip user={this.__user} />;
  }
}

export function $createMentionNode(user: MentionUser, text: string): MentionNode {
  return $create(MentionNode).setMention(user, text);
}

export function $isMentionNode(node: LexicalNode | null | undefined): node is MentionNode {
  return node instanceof MentionNode;
}
